import React, { useEffect } from 'react';
import { filterNullish } from '@modernhealth/filter-nullish';
import {
    BodyTextSmall,
    HeadingText50,
    SectionHeaderSkeleton,
} from '@modernhealth/vital-components';
import { FetchSessionsByDateTimeQuery } from '@modernhealth/circles-graphql/v2';

import { captureException } from 'src/shared/helpers/captureException';

import { useGetMonthsData } from '../hooks/useGetMonthsData';

import { MonthSection } from './MonthSection';
import { SessionCardRowSkeleton } from './SessionCardSkeletons';

export const UpcomingSessionsZeroState = () => {
    return (
        <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <HeadingText50>No upcoming sessions</HeadingText50>
            <BodyTextSmall>There are no scheduled sessions in the near future.</BodyTextSmall>
        </div>
    );
};

type Month = {
    monthStart: string;
    monthEnd: string;
    upcomingYear: boolean;
};

const DisplayUpcomingByTime: React.FC<{ currentMoment: string }> = ({ currentMoment }) => {
    const numberOfMonthsToDisplay = 3;
    const byTimeData = useGetMonthsData(numberOfMonthsToDisplay, currentMoment);

    const monthsData = byTimeData.data?.map(
        (month: {
            key: Month;
            data: {
                circleSessionsByDateTime:
                    | FetchSessionsByDateTimeQuery['circleSessionsByDateTime']
                    | null;
            };
        }) => {
            return {
                connection: month?.data
                    .circleSessionsByDateTime as FetchSessionsByDateTimeQuery['circleSessionsByDateTime'],
                monthStartDate: month.key.monthStart,
                upcomingYear: month.key.upcomingYear,
            };
        },
    );
    useEffect(() => {
        if (!monthsData || byTimeData.hasError) {
            captureException(new Error('Circles failed to load'));
        }
    }, [monthsData, byTimeData.hasError]);
    if (byTimeData.loading) {
        return (
            <div>
                <SectionHeaderSkeleton />
                <SessionCardRowSkeleton />

                <SectionHeaderSkeleton />
                <SessionCardRowSkeleton />

                <SectionHeaderSkeleton />
                <SessionCardRowSkeleton />
            </div>
        );
    }

    const sessionCount = monthsData?.reduce((a, b) => a + b.connection?.totalCount, 0);

    if (!sessionCount) return <UpcomingSessionsZeroState />;
    return (
        <>
            {monthsData?.map((month, i) => {
                const sessions = filterNullish(
                    month.connection?.edges.map(edge => edge?.node) ?? [],
                );

                return (
                    <MonthSection
                        key={`${month.monthStartDate}-${i}`}
                        monthStartDate={month.monthStartDate}
                        upcomingYear={month.upcomingYear}
                        sessions={sessions}
                    />
                );
            })}
        </>
    );
};

export default DisplayUpcomingByTime;
